import {
  ArrowUpward,
  ChevronLeft,
  ChevronRight,
  Clear,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import { FranchiseDocumentType } from "@hubcapp/views/MyDetails/types";
import { Paper, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { TableCellActions } from "@hubcapp/views/MyDetails/components/TableCellActions";
import { format } from "date-fns";
import MaterialTable, { Icons, MTableBodyRow as Row } from "material-table";
import React, { forwardRef } from "react";
import clsx from "clsx";

const tableIcons: Icons = {
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles(() => ({
  tableRow: {
    "&.mobileTableRow *": {
      fontSize: "12px !important",
      maxWidth: "156px",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
}));

interface Props {
  loading: boolean;
  data: FranchiseDocumentType[];
}

export const FranchiseTable: React.FC<Props> = ({ loading, data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <MaterialTable
      isLoading={loading}
      icons={tableIcons}
      columns={[
        {
          title: "Document",
          field: "documentType",
          sorting: true,
        },
        {
          title: "Franchise",
          field: "agreementName",
          sorting: true,
        },
        {
          title: "Start date",
          field: "fromDate",
          sorting: true,
          width: "10%",
          render: (rowData: FranchiseDocumentType) =>
            rowData.fromDate
              ? format(new Date(rowData.fromDate), "dd/MM/yyyy")
              : "",
        },
        {
          title: "End date",
          field: "toDate",
          sorting: true,
          width: "10%",
          render: (rowData: FranchiseDocumentType) =>
            rowData.toDate
              ? format(new Date(rowData.toDate), "dd/MM/yyyy")
              : "",
        },
        {
          title: "Actions",
          field: "actions",
          width: mobileDevice ? "77px" : "auto",
          sorting: false,
          render: (rowData: FranchiseDocumentType) => (
            <TableCellActions rowData={rowData} />
          ),
        },
      ].filter(
        tab => !mobileDevice || !["fromDate", "toDate"].includes(tab.field),
      )}
      options={{
        actionsColumnIndex: -1,
        draggable: false,
        headerStyle: {
          whiteSpace: "nowrap",
        },
        paging: true,
        pageSize: 10,
        emptyRowsWhenPaging: false,
        detailPanelColumnAlignment: "right",
        tableLayout: "auto",
      }}
      localization={{
        header: { actions: "Action" },
        body: {
          emptyDataSourceMessage: loading ? "" : "No documents to display",
        },
      }}
      components={{
        Toolbar: () => null,
        Container: props => <Paper {...props} elevation={1} />,
        Row: props => (
          <Row
            className={clsx(classes.tableRow, {
              mobileTableRow: mobileDevice,
            })}
            data-test={`learners-table-row-${props.index}`}
            {...props}
          />
        ),
      }}
      data={data}
    />
  );
};
